import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { format } from "date-fns";

import Page from "./Page";
import useExpense from "../../hooks/useExpense";
import useSupervisors from "../../hooks/useSupervisors";
import useProject from "../../hooks/useProject";
import useExpenseAccount from "../../hooks/useExpenseAccount";
import ExpenseApprovalModal from "../modals/ExpenseApprovalModal";
import { getId } from "../../utils/idHelpers";

const ExpensesDashboard = () => {
  const navigate = useNavigate();
  const { userId: currentUserId } = useSelector(
    (state) => state.authentication,
  );

  const {
    pendingExpenses,
    loading: expensesLoading,
    getPendingApprovalsDashboard,
    approveExpense,
    rejectExpense,
    getById: getExpenseById,
  } = useExpense();
  const { supervisors = [] } = useSupervisors();
  const { projects = [], getProjects } = useProject();
  const { expenseAccounts = [], loadExpenseAccounts } = useExpenseAccount();

  const safeSupervisors = Array.isArray(supervisors) ? supervisors : [];
  const safeProjects = Array.isArray(projects) ? projects : [];
  const safePendingExpenses = Array.isArray(pendingExpenses)
    ? pendingExpenses
    : [];

  const [selectedSupervisorId, setSelectedSupervisorId] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState("");

  const [approvalDialog, setApprovalDialog] = useState({
    open: false,
    expense: null,
    initialAction: "approve",
  });
  const [approvalSubmitting, setApprovalSubmitting] = useState(false);

  const [receiptDialog, setReceiptDialog] = useState({
    open: false,
    receipts: [],
    expenseDescription: "",
  });

  const safeReceipts = Array.isArray(receiptDialog.receipts)
    ? receiptDialog.receipts
    : [];

  useEffect(() => {
    if (!currentUserId) return;

    const isManagerInList = safeSupervisors.some(
      (supervisor) =>
        String(supervisor.id || supervisor._id || "") === String(currentUserId),
    );

    if (isManagerInList) {
      setSelectedSupervisorId(currentUserId);
    }
  }, [currentUserId, safeSupervisors]);

  useEffect(() => {
    getProjects();
    loadExpenseAccounts();
  }, []);

  useEffect(() => {
    loadExpenses();
  }, [selectedSupervisorId, selectedProjectId]);

  const loadExpenses = async () => {
    await getPendingApprovalsDashboard({
      supervisorId: selectedSupervisorId || null,
      projectId: selectedProjectId || null,
    });
  };

  const handleToolBarClick = (item) => {
    switch (item) {
      case "callDashboard":
        navigate("/home");
        break;
      case "syncDashboard":
        navigate("/syncdashboard");
        break;
      case "surveyDashboard":
        navigate("/surveymetrics");
        break;
      case "orderDashboard":
        navigate("/orderdashboard");
        break;
      case "clientDashboard":
        navigate("/clientdashboard");
        break;
      case "repDashboard":
        navigate("/repdashboard");
        break;
      default:
        break;
    }
  };

  const openApprovalDialog = async (expense, initialAction) => {
    const expenseId = getId(expense);
    const fetchedExpense = await getExpenseById(expenseId);
    const pick = (value, fallback) =>
      value === undefined || value === null ? fallback : value;

    const fullExpense = fetchedExpense
      ? {
          ...expense,
          ...fetchedExpense,
          // Keep populated field values if detail endpoint omits or nulls them.
          projectId: pick(fetchedExpense.projectId, expense.projectId),
          expenseAccountId: pick(
            fetchedExpense.expenseAccountId,
            expense.expenseAccountId,
          ),
          expenseDate: pick(fetchedExpense.expenseDate, expense.expenseDate),
          description: pick(fetchedExpense.description, expense.description),
          notes: pick(fetchedExpense.notes, expense.notes),
          amount: pick(fetchedExpense.amount, expense.amount),
          minutes: pick(fetchedExpense.minutes, expense.minutes),
          mileage: pick(fetchedExpense.mileage, expense.mileage),
          pst: pick(fetchedExpense.pst, expense.pst),
          hst: pick(fetchedExpense.hst, expense.hst),
          gst: pick(fetchedExpense.gst, expense.gst),
          receipts: pick(fetchedExpense.receipts, expense.receipts),
          expenseCategory: pick(
            fetchedExpense.expenseCategory,
            expense.expenseCategory,
          ),
          expenseType: pick(fetchedExpense.expenseType, expense.expenseType),
          project: pick(fetchedExpense.project, expense.project),
          expenseAccount: pick(
            fetchedExpense.expenseAccount,
            expense.expenseAccount,
          ),
          user: pick(fetchedExpense.user, expense.user),
        }
      : expense;
    setApprovalDialog({
      open: true,
      expense: fullExpense,
      initialAction,
    });
  };

  const closeApprovalDialog = () => {
    setApprovalDialog({ open: false, expense: null, initialAction: "approve" });
  };

  const handleApproveExpense = async (editedFields) => {
    if (!approvalDialog.expense) return;
    setApprovalSubmitting(true);
    try {
      await approveExpense(
        getId(approvalDialog.expense),
        currentUserId || "system",
        editedFields?.notes || "",
        editedFields,
      );
      closeApprovalDialog();
      await loadExpenses();
    } finally {
      setApprovalSubmitting(false);
    }
  };

  const handleRejectExpense = async (rejectionReason, editedFields) => {
    if (!approvalDialog.expense) return;
    setApprovalSubmitting(true);
    try {
      await rejectExpense(
        getId(approvalDialog.expense),
        currentUserId || "system",
        rejectionReason,
        editedFields,
      );
      closeApprovalDialog();
      await loadExpenses();
    } finally {
      setApprovalSubmitting(false);
    }
  };

  const handleViewReceipts = async (expenseId, description) => {
    const expense = await getExpenseById(expenseId);
    if (
      expense &&
      Array.isArray(expense.receipts) &&
      expense.receipts.length > 0
    ) {
      setReceiptDialog({
        open: true,
        receipts: expense.receipts,
        expenseDescription: description || expense.description || "Expense",
      });
    }
  };

  const formatCurrency = (value) => `$${value?.toFixed(2) || "0.00"}`;

  const getExpenseTypeLabel = (expense) => {
    return (
      expense?.expenseAccount?.name ||
      expense?.expenseCategory ||
      expense?.expenseType ||
      "-"
    );
  };

  const formatDate = (date) => {
    if (!date) return "";
    return format(new Date(date), "MMM dd, yyyy");
  };

  return (
    <>
      <Page
        pageName="expenses"
        includeToolbar={true}
        onToolItemClick={handleToolBarClick}
        items={[
          { name: "callDashboard", iconName: "CalendarMonth" },
          { name: "syncDashboard", iconName: "CloudSync" },
          { name: "surveyDashboard", iconName: "Assignment" },
          { name: "orderDashboard", iconName: "ShoppingCart" },
          { name: "clientDashboard", iconName: "Business" },
          { name: "repDashboard", iconName: "People" },
        ]}
      >
        <Box sx={{ mb: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
          <FormControl sx={{ minWidth: 260 }}>
            <InputLabel>Supervisor</InputLabel>
            <Select
              value={selectedSupervisorId}
              label="Supervisor"
              onChange={(e) => setSelectedSupervisorId(e.target.value)}
            >
              <MenuItem value="">
                <em>All Supervisors</em>
              </MenuItem>
              {safeSupervisors.map((supervisor) => (
                <MenuItem
                  key={supervisor.id}
                  value={supervisor.id}
                >
                  {supervisor.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 300 }}>
            <InputLabel>Project</InputLabel>
            <Select
              value={selectedProjectId}
              label="Project"
              onChange={(e) => setSelectedProjectId(e.target.value)}
            >
              <MenuItem value="">
                <em>All Projects</em>
              </MenuItem>
              {safeProjects.map((project) => (
                <MenuItem
                  key={project.id}
                  value={project.id}
                >
                  {project.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box>
          <Typography
            variant="h6"
            gutterBottom
          >
            Pending Expense Approvals ({safePendingExpenses.length})
          </Typography>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #ddd" }}>
                <th style={{ padding: "8px", textAlign: "left" }}>Date</th>
                <th style={{ padding: "8px", textAlign: "left" }}>Rep</th>
                <th style={{ padding: "8px", textAlign: "left" }}>Project</th>
                <th style={{ padding: "8px", textAlign: "left" }}>Type</th>
                <th style={{ padding: "8px", textAlign: "left" }}>
                  Description
                </th>
                <th style={{ padding: "8px", textAlign: "right" }}>Amount</th>
                <th style={{ padding: "8px", textAlign: "center" }}>Receipt</th>
                <th style={{ padding: "8px", textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {safePendingExpenses.map((expense) => (
                <tr
                  key={getId(expense)}
                  style={{ borderBottom: "1px solid #eee" }}
                >
                  <td style={{ padding: "8px" }}>
                    <Typography variant="body2">
                      {formatDate(expense.expenseDate)}
                    </Typography>
                  </td>
                  <td style={{ padding: "8px" }}>
                    <Typography variant="body2">
                      {expense.user?.legalName ||
                        expense.user?.name ||
                        "Unknown"}
                    </Typography>
                  </td>
                  <td style={{ padding: "8px" }}>
                    <Typography variant="body2">
                      {expense.project?.name || "-"}
                    </Typography>
                  </td>
                  <td style={{ padding: "8px" }}>
                    <Typography variant="body2">
                      {getExpenseTypeLabel(expense)}
                    </Typography>
                  </td>
                  <td style={{ padding: "8px" }}>
                    <Typography
                      variant="body2"
                      noWrap
                    >
                      {expense.description}
                    </Typography>
                  </td>
                  <td style={{ padding: "8px", textAlign: "right" }}>
                    <Typography variant="body2">
                      {expense.expenseCategory === "hours"
                        ? `${expense.minutes || 0} min`
                        : expense.expenseCategory === "mileage"
                          ? `${expense.mileage || 0} km`
                          : formatCurrency(expense.amount)}
                    </Typography>
                  </td>
                  <td style={{ padding: "8px", textAlign: "center" }}>
                    {expense.receipts && expense.receipts.length > 0 ? (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handleViewReceipts(
                            getId(expense),
                            expense.description,
                          );
                        }}
                        style={{
                          color: "#1976d2",
                          textDecoration: "none",
                          fontSize: "0.875rem",
                          cursor: "pointer",
                        }}
                      >
                        View
                      </a>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                      >
                        -
                      </Typography>
                    )}
                  </td>
                  <td style={{ padding: "8px", textAlign: "center" }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="center"
                    >
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={() => openApprovalDialog(expense, "approve")}
                        disabled={expensesLoading}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => openApprovalDialog(expense, "reject")}
                        disabled={expensesLoading}
                      >
                        Reject
                      </Button>
                    </Stack>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </Page>

      <ExpenseApprovalModal
        open={approvalDialog.open}
        expense={approvalDialog.expense}
        initialAction={approvalDialog.initialAction}
        projects={safeProjects}
        expenseAccounts={expenseAccounts}
        submitting={approvalSubmitting}
        onClose={closeApprovalDialog}
        onApprove={handleApproveExpense}
        onReject={handleRejectExpense}
      />

      <Dialog
        open={receiptDialog.open}
        onClose={() =>
          setReceiptDialog({
            open: false,
            receipts: [],
            expenseDescription: "",
          })
        }
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Receipts - {receiptDialog.expenseDescription}</DialogTitle>
        <DialogContent>
          {safeReceipts.map((receipt, index) => (
            <Box
              key={index}
              sx={{ mb: 2 }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
              >
                Receipt {index + 1}
              </Typography>
              <Box
                sx={{
                  mt: 1,
                  border: "1px solid #ddd",
                  borderRadius: 1,
                  overflow: "hidden",
                }}
              >
                <img
                  src={receipt}
                  alt={`Receipt ${index + 1}`}
                  style={{
                    width: "100%",
                    height: "auto",
                    display: "block",
                  }}
                />
              </Box>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setReceiptDialog({
                open: false,
                receipts: [],
                expenseDescription: "",
              })
            }
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ExpensesDashboard;
